import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, Validators } from '@angular/forms';
import { DrawerType } from '@shared-features/enums/layout.enum';
import { ColumnOptionsService } from '../../services/column-options.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { enumToArray } from '../../utils/enum-to-array.util';
import { EnumModel } from '@shared-features/models/enum-model.model';
import { StorageConstant } from '@shared/constants';
import { LocalStorageUtil } from '@shared/utils';

const DEFAULT_SUBTITLE_KEY = 'Reports.ColumnSettingsSubtitle';

@Component({
  selector: 'app-column-options',
  templateUrl: './column-options.component.html',
  styleUrls: ['./column-options.component.scss'],
})
export class ColumnOptionsComponent extends BaseComponent implements OnInit {
  @Output() drawerOpened = new EventEmitter<{ open: true; type: DrawerType }>();
  @Output() drawerClosed = new EventEmitter<void>();
  @Output() showDynamicFieldsChanged = new EventEmitter<boolean>();

  /**
   * Mandatory columns: cannot be removed, cannot be reordered.
   *
   * Supplied per list through the column state, not as a binding — this component is a single
   * instance in the layout, so there is no per-page binding site. Empty for every existing list,
   * which makes every guard below a no-op and leaves their behaviour byte-identical.
   */
  lockedKeys: string[] = [];

  /**
   * The arrangement `Reset` restores.
   *
   * Null for every existing list, preserving today's behaviour: `resetOptions()` re-reads the
   * SAVED state, meaning Reset effectively does nothing. That is arguably a bug, but consumers
   * may rely on it, so it changes only for a list that explicitly supplies a default.
   */
  defaultState: { key: string; order: number }[] | null = null;

  /**
   * Hides the whole "Show dynamic fields" section for a list that does not consume the toggle.
   *
   * Supplied through the column state rather than as an @Input, for the same reason as `lockedKeys`:
   * this component is a single instance in the layout, so there is no per-page binding site. False for
   * every existing list, so the section keeps rendering exactly as before.
   */
  hideDynamicFieldsToggle = false;

  /**
   * Labels to render VERBATIM, keyed by column key.
   *
   * A column listed here skips `'Reports.' + suffix | translate` entirely. That is required for a
   * column whose name is data the user typed - a customer dynamic field - because there is no
   * translation key to look up, so the lookup would render the missing key itself.
   *
   * Absent for every pre-existing list, so they all stay fully translated.
   *
   * Supplied through the column state for the same reason as `lockedKeys`: this component is a
   * single instance in the layout, so there is no per-page binding site.
   */
  rawLabels: Record<string, string> = {};

  /** Panel subtitle, per list. Defaults to the shipment-list wording every other list uses. */
  subtitleKey = DEFAULT_SUBTITLE_KEY;

  listName: string;
  keys: any = {};
  state: { key: string; order: number }[];
  availableColumns: EnumModel[] = [];
  form: FormArray;
  isShown = false;
  destroy$ = new Subject();
  draggedColumnOrder = -1;
  draggedOverColumnOrder = -1;

  draggedDynamicOrder = -1;
  draggedOverDynamicOrder = -1;
  
  isExpanded = false;
  isDynamicFieldsVisible = LocalStorageUtil.getItem(StorageConstant.HAS_DYNAMIC_FIELDS);
  dynamicFields = LocalStorageUtil.getItem(StorageConstant.DYNAMIC_FIELDS) || [];
  private originalDynamicFields: any[] = [];

  searchTerm = '';

  constructor(private columnOptionsService: ColumnOptionsService) {
    super();
  }

  ngOnInit(): void {
    this.columnOptionsService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name, keys, state, lockedKeys, defaultState, hideDynamicFieldsToggle, rawLabels, subtitleKey }) => {
        this.listName = name;
        this.keys = keys || {};
        this.state = state || [];
        this.lockedKeys = lockedKeys || [];
        this.defaultState = defaultState || null;
        this.hideDynamicFieldsToggle = !!hideDynamicFieldsToggle;
        this.rawLabels = rawLabels || {};
        this.subtitleKey = subtitleKey || DEFAULT_SUBTITLE_KEY;
        this.resetForm();
      });

    this.columnOptionsService.drawer$.subscribe((shown: boolean) => {
      if (!shown && this.isShown) {
        this.closeDrawer();
      } else if (shown) {
        this.onSearchChange('');
        this.drawerOpened.emit({ open: true, type: DrawerType.ColumnOptions });
        this.columnOptionsService.dynamicColumns$.pipe(takeUntil(this.destroy$)).subscribe(columns => {
          this.dynamicFields=columns||[]
        });
      }
      this.isShown = shown;
    });
  }

  resetForm(): void {
    this.form = new FormArray([]);

    (this.state || []).sort((a, b) => a.order - b.order);
    (this.state || []).forEach((item) => {
      this.form.push(new FormControl(item.key, [Validators.required]));
    });

    this.form.valueChanges.subscribe((values) => {
      this.state = (values || []).map((key, order) => ({ key, order }));
    });

    this.refreshAvailableColumns();
  }

  labelFor(key: string): string {
    return this.rawLabels[key] || this.translate('Reports.' + this.keys[key]);
  }

  matchesSearch(key: string): boolean {
    const term = (this.searchTerm || '').trim().toLowerCase();
    return !term || this.labelFor(key).toLowerCase().includes(term);
  }



  private refreshAvailableColumns(): void {
    const used = new Set((this.form?.value as string[]) || []);
    this.availableColumns = enumToArray(this.keys).filter((option) => !used.has(option.key) && this.matchesSearch(option.key));
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.refreshAvailableColumns();
  }

  trackByOptionKey(_index: number, option: EnumModel): string {
    return option.key;
  }

  trackByControlValue(_index: number, control: AbstractControl): string {
    return control.value;
  }

  addColumn(key: string): void {
    const order = (this.state || []).length;
    this.state = [...(this.state || []), { key, order }];
    this.resetForm();
  }

  applyChanges(): void {
    this.columnOptionsService.state = { name: this.listName, keys: this.keys, state: this.state };
    this.columnOptionsService.toggleDynamicFields(this.isDynamicFieldsVisible);
    this.columnOptionsService.setDynamicColumns([...this.dynamicFields]);
    LocalStorageUtil.setItem(StorageConstant.DYNAMIC_FIELDS, this.dynamicFields || []);
    this.originalDynamicFields = [];

    // Persists the list being applied, and only that one. A list with no registered persister is a
    // no-op, so every existing list stays localStorage-only. Passing the CURRENT list matters:
    // without it, applying columns on the shipments list would upload the bulk list's arrangement as
    // a side effect once bulk had been visited in the same session.
    this.columnOptionsService
      .saveStorage(this.listName)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe();

    this.closeDrawer();
  }

  closeDrawer(): void {
    if (this.originalDynamicFields.length > 0) {
      this.dynamicFields = JSON.parse(JSON.stringify(this.originalDynamicFields));
      this.originalDynamicFields = [];
    }
    this.onSearchChange('');
    this.drawerClosed.emit();
  }

  /** A locked column may not be removed or dragged (FR-037). */
  isLocked(key: string): boolean {
    return (this.lockedKeys || []).includes(key);
  }

  removeColumn(key, order): void {
    if (this.isLocked(key)) {
      return;
    }
    const state = this.state.filter((item) => item.key !== key);

    state.sort((a, b) => a.order - b.order);
    this.state = state.map((item) => ({
      ...item,
      order: item.order < order ? item.order : item.order - 1,
    }));

    this.resetForm();
  }

  /**
   * Reset: restore this list's DEFAULT arrangement.
   *
   * Local to the panel — nothing is written to the service, localStorage or the API until Apply, so
   * the grid keeps its current columns until then.
   *
   * When a caller supplies defaultState, Reset genuinely restores the defaults. Without it the
   * original behaviour is preserved: re-read the saved state, which is effectively a no-op.
   */
  resetOptions(): void {
    const { keys, state } = this.columnOptionsService.getStateByName(this.listName);
    this.keys = keys;
    this.state = this.defaultState ? this.defaultState.map((item) => ({ ...item })) : state || [];
    this.resetForm();
  }

  /**
   * Cancel: discard the edits made in this panel, which means the SAVED arrangement — never the
   * defaults.
   *
   * Separate from `resetOptions` on purpose. Cancel used to call it, which was harmless only while no
   * list supplied a `defaultState`: without one, `resetOptions` re-reads the saved state and the two
   * intents coincide. For a list that does supply defaults, sharing the method made Cancel silently
   * reset the arrangement to defaults instead of abandoning the edits — and because this panel
   * re-reads state only when `state$` emits, that reset arrangement survived until the next Apply
   * committed it over the user's saved arrangement.
   *
   * Behaviour is unchanged for every list without a `defaultState`: both paths read the same state.
   */
  discardChanges(): void {
    const { keys, state } = this.columnOptionsService.getStateByName(this.listName);
    this.keys = keys;
    this.state = (state || []).map((item) => ({ ...item }));
    this.resetForm();
  }

  swapOrder(): void {
    if (this.draggedOverColumnOrder > -1 && this.draggedColumnOrder > -1) {
      const draggedKey = this.state[this.draggedColumnOrder]?.key;
      const draggedOverKey = this.state[this.draggedOverColumnOrder]?.key;

      // Abort if either end of the swap is locked — dropping ONTO a locked column would displace
      // it just as surely as dragging it away.
      if (this.isLocked(draggedKey) || this.isLocked(draggedOverKey)) {
        this.draggedOverColumnOrder = -1;
        this.draggedColumnOrder = -1;
        return;
      }
      this.state = this.state.map((item) => {
        if (item.order === this.draggedColumnOrder) {
          return { ...item, key: draggedOverKey };
        } else if (item.order === this.draggedOverColumnOrder) {
          return { ...item, key: draggedKey };
        }
        return { ...item };
      });

      this.draggedOverColumnOrder = -1;
      this.draggedColumnOrder = -1;
      this.resetForm();
    }
  }
  swapDynamicFields(): void {
    if (this.draggedDynamicOrder > -1 && this.draggedOverDynamicOrder > -1) {
      if (this.originalDynamicFields.length === 0) {
        this.originalDynamicFields = JSON.parse(JSON.stringify(this.dynamicFields));
      }
      const temp = this.dynamicFields[this.draggedDynamicOrder];
      this.dynamicFields[this.draggedDynamicOrder] = this.dynamicFields[this.draggedOverDynamicOrder];
      this.dynamicFields[this.draggedOverDynamicOrder] = temp;

      this.draggedDynamicOrder = -1;
      this.draggedOverDynamicOrder = -1;
    }
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  onToggleShowDynamicFields(event: any): void {
    this.isDynamicFieldsVisible = event.checked;
    LocalStorageUtil.setItem(StorageConstant.HAS_DYNAMIC_FIELDS, this.isDynamicFieldsVisible || false);
    this.columnOptionsService.toggleDynamicFields(this.isDynamicFieldsVisible);
    // Deliberately does NOT close the drawer. The dynamic-field list lives inside this panel, so closing
    // on toggle hid the very thing the toggle had just changed. The state is already published through
    // `toggleDynamicFields`, so nothing depended on the close to take effect.
  }
}
